import React, { createContext, useContext, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../lib/queryClient";
import { useNavigate, useLocation } from "react-router-dom";
import type { User } from "@shared/schema";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; firstName: string; lastName: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  apiKey: string | null;
  setApiKey: React.Dispatch<React.SetStateAction<string | null>>;
  openaiApiKey: string | null;
  setOpenaiApiKey: React.Dispatch<React.SetStateAction<string | null>>;
  deepseekApiKey: string | null;
  setDeepseekApiKey: React.Dispatch<React.SetStateAction<string | null>>;
}

interface AuthResponse {
  user: User;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [openaiApiKey, setOpenaiApiKey] = useState<string | null>(null);
  const [deepseekApiKey, setDeepseekApiKey] = useState<string | null>(null);

  const { data: user, isLoading, refetch } = useQuery<AuthResponse>({
    queryKey: ["/api/auth/me"],
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    retryOnMount: false,
  });

  useEffect(() => {
    if (user?.user) {
      setApiKey(user.user.apiKey);
      setOpenaiApiKey(user.user.openaiApiKey);
      setDeepseekApiKey(user.user.deepseekApiKey);
    }
  }, [user]);

  // Handle OAuth redirects - refetch auth status when coming from OAuth callback
  useEffect(() => {
    if (location.pathname === '/dashboard' && !user && !isLoading) {
      // If we're on dashboard but no user data, it might be an OAuth redirect
      // Force a refetch of the auth status
      refetch();
    }
  }, [location.pathname, user, isLoading, refetch]);

  const loginMutation = useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const response = await apiRequest("/api/auth/login", "POST", data);
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/auth/me"], data);
      navigate("/dashboard");
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: { email: string; firstName: string; lastName: string; password: string }) => {
      const response = await apiRequest("/api/auth/register", "POST", data);
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["/api/auth/me"], data);
      navigate("/dashboard");
    },
  });

  const login = async (email: string, password: string) => {
    await loginMutation.mutateAsync({ email, password });
  };

  const register = async (data: { email: string; firstName: string; lastName: string; password: string }) => {
    await registerMutation.mutateAsync(data);
  };

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("/api/auth/logout", "POST");
    },
    onSuccess: () => {
      queryClient.setQueryData(["/api/auth/me"], null);
      queryClient.clear();
      navigate("/");
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user?.user || null,
        isLoading,
        login,
        register,
        logout: logoutMutation.mutateAsync,
        apiKey,
        setApiKey,
        openaiApiKey,
        setOpenaiApiKey,
        deepseekApiKey,
        setDeepseekApiKey,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}